import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Sector } from 'graphql-common';
import { OrderingParams, PaginationParams } from '@lib/enums/urls';
import useSectorsQueryHook from 'utils/fetch/hooks/useSectorsQueryHook';
import {
  PerformanceWidget,
  defaultPaginationState,
  ORDERING_PARAMS,
} from '../PerformanceWidget';
import getPerformanceWidgetColumns, {
  PerformanceWidgetEntityType,
} from '../PerformanceWidget/utils/getPerformanceWidgetColumns';

export interface SectorsPerformanceWidgetProps {
  title?: string;
  showBadge?: boolean;
}

export default function SectorsPerformanceWidget({
  title,
  showBadge = false,
}: SectorsPerformanceWidgetProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const orderingState = useState<OrderingParams>({ ...ORDERING_PARAMS });
  const paginationState = useState<PaginationParams>(defaultPaginationState);
  const [orderingParams] = orderingState;
  const [paginationParams] = paginationState;

  const {
    collection: sectors,
    totalCount,
    loading,
    error,
  } = useSectorsQueryHook({
    fetchPolicy: 'cache-and-network',
    paginationParams: {
      ...paginationParams,
      ...orderingParams,
    },
    searchQuery,
    includeFullData: false,
    includePerformanceDataByTaskDeadlinesMissed: true,
  });

  const columns = useMemo(
    () =>
      getPerformanceWidgetColumns({
        t,
        entityType: PerformanceWidgetEntityType.Sector,
      }),
    [t],
  );

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return (
    <PerformanceWidget
      title={title || t('sector-deadline-efficiency-tracker')}
      showBadge={showBadge}
      searchPlaceholder={t('search-by-sector-name')}
      data={sectors as Sector[]}
      totalCount={totalCount}
      loading={loading}
      error={error}
      columns={columns}
      onSearchChange={handleSearchChange}
      paginationState={paginationState}
      orderingState={orderingState}
    />
  );
}
